import React, { useState } from 'react'

const API = process.env.REACT_APP_API_URL || 'https://sunshoos-v2.onrender.com'

const BLANK_DIRECTOR = { name: '', role: 'Director', appointed: '', nationality: '', occupation: '' }
const BLANK_SHAREHOLDER = { name: '', share_class: 'Ordinary', shares_held: '', percentage: '' }

const SIC_COMMON = [
  { code: '62012', desc: 'Business and domestic software development' },
  { code: '62020', desc: 'IT consultancy' },
  { code: '69201', desc: 'Accounting and auditing' },
  { code: '69202', desc: 'Bookkeeping' },
  { code: '70229', desc: 'Management consultancy' },
  { code: '74909', desc: 'Other professional services' },
  { code: '47910', desc: 'Retail via mail order or internet' },
  { code: '41100', desc: 'Property development' },
  { code: '68209', desc: 'Other letting and operating of own property' },
  { code: '56101', desc: 'Restaurants and cafes' },
  { code: '43210', desc: 'Electrical installation' },
  { code: '43220', desc: 'Plumbing, heating and air-conditioning' },
  { code: '49410', desc: 'Freight transport by road' },
  { code: '86210', desc: 'General medical practice' },
  { code: '85590', desc: 'Other education' },
]

export default function ConfirmationStatement() {
  const [tab, setTab] = useState('manual')
  const [loading, setLoading] = useState(false)
  const [chLoading, setChLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [saved, setSaved] = useState(false)
  const [sicSearch, setSicSearch] = useState('')

  // Company details
  const [company, setCompany] = useState({
    name: '', number: '', registered_address: '',
    incorporation_date: '', confirmation_date: '',
    review_period_start: '', review_period_end: '',
    sic_codes: [],
  })
  const setC = (k, v) => setCompany(c => ({ ...c, [k]: v }))

  // Directors
  const [directors, setDirectors] = useState([{ ...BLANK_DIRECTOR }])
  const setDir = (i, k, v) => setDirectors(d => d.map((x, idx) => idx === i ? { ...x, [k]: v } : x))
  const addDir = () => setDirectors(d => [...d, { ...BLANK_DIRECTOR }])
  const removeDir = i => setDirectors(d => d.filter((_, idx) => idx !== i))

  // Shareholders
  const [shareholders, setShareholders] = useState([{ ...BLANK_SHAREHOLDER }])
  const setSh = (i, k, v) => setShareholders(s => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x))
  const addSh = () => setShareholders(s => [...s, { ...BLANK_SHAREHOLDER }])
  const removeSh = i => setShareholders(s => s.filter((_, idx) => idx !== i))

  // Companies House lookup
  const [chNumber, setChNumber] = useState('')
  async function handleCHLookup() {
    if (!chNumber.trim()) { setError('Enter a company number'); return }
    setChLoading(true); setError('')
    try {
      const res = await fetch(`${API}/api/confirmation/ch-lookup?company_number=${chNumber.trim()}`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Companies House lookup failed')
      // Pre-fill form
      setCompany(c => ({
        ...c,
        name: data.company_name || c.name,
        number: data.company_number || c.number,
        registered_address: data.registered_address || c.registered_address,
        incorporation_date: data.date_of_creation || c.incorporation_date,
        sic_codes: data.sic_codes || c.sic_codes,
      }))
      if (data.directors?.length) setDirectors(data.directors.map(d => ({ name: d.name || '', role: d.officer_role || 'Director', appointed: d.appointed_on || '', nationality: d.nationality || '', occupation: d.occupation || '' })))
      setTab('manual')
    } catch (e) { setError(e.message) }
    setChLoading(false)
  }

  // SIC code toggle
  const toggleSic = (code) => {
    setCompany(c => ({
      ...c,
      sic_codes: c.sic_codes.includes(code)
        ? c.sic_codes.filter(s => s !== code)
        : [...c.sic_codes, code]
    }))
  }

  async function handleSubmit() {
    setLoading(true); setError(''); setResult(null); setSaved(false)
    try {
      const res = await fetch(`${API}/api/confirmation/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company, directors, shareholders })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  async function handleExport(type) {
    try {
      const res = await fetch(`${API}/api/confirmation/export-${type}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error(`${type} export failed`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `CS01_${(result.company?.name || 'Company').replace(/ /g, '_')}_${(result.company?.confirmation_date || '').replace(/ /g, '_')}.${type === 'excel' ? 'xlsx' : 'pdf'}`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError(`Export error: ${e.message}`) }
  }

  async function handleSave() {
    try {
      const res = await fetch(`${API}/api/confirmation/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Save failed')
      setSaved(true)
    } catch (e) { setError('Save error: ' + e.message) }
  }

  const s = {
    page: { padding: '24px', maxWidth: 1020, margin: '0 auto', fontFamily: 'Calibri, sans-serif' },
    card: { background: '#0f1923', border: '1px solid rgba(201,151,58,0.15)', borderRadius: 10, padding: 22, marginBottom: 18 },
    row: { display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
    label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    input: { background: '#0B1929', border: '1px solid rgba(201,151,58,0.2)', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    tabBar: { display: 'flex', gap: 2, marginBottom: 18, background: '#0B1929', borderRadius: 8, padding: 4 },
    tab: a => ({ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: a ? '#C9973A' : 'transparent', color: a ? '#0B1929' : 'rgba(255,255,255,0.4)', transition: 'all .2s' }),
    btn: (v = 'primary') => ({ padding: '10px 20px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: v === 'primary' ? '#C9973A' : v === 'green' ? '#1a4a2e' : v === 'blue' ? '#0f2a4a' : v === 'red' ? '#4a1a1a' : '#1a2a4a', color: v === 'primary' ? '#0B1929' : v === 'green' ? '#4ade80' : v === 'blue' ? '#93c5fd' : v === 'red' ? '#f87171' : '#C9973A' }),
    sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(201,151,58,0.7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    sicChip: (sel) => ({ padding: '5px 10px', borderRadius: 20, border: `1px solid ${sel ? '#C9973A' : 'rgba(255,255,255,0.1)'}`, cursor: 'pointer', fontSize: 11, background: sel ? 'rgba(201,151,58,0.15)' : 'transparent', color: sel ? '#C9973A' : 'rgba(255,255,255,0.45)', marginRight: 6, marginBottom: 6, display: 'inline-block' }),
    checkRow: (status) => ({ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 7, marginBottom: 6, background: status === 'ok' ? 'rgba(74,222,128,0.05)' : status === 'action' ? 'rgba(251,191,36,0.05)' : 'rgba(59,130,246,0.05)', border: `1px solid ${status === 'ok' ? 'rgba(74,222,128,0.15)' : status === 'action' ? 'rgba(251,191,36,0.15)' : 'rgba(59,130,246,0.15)'}` }),
  }

  const filteredSic = SIC_COMMON.filter(s =>
    !sicSearch || s.code.includes(sicSearch) || s.desc.toLowerCase().includes(sicSearch.toLowerCase())
  )

  return (
    <div style={s.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>Confirmation Statement</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0' }}>
          CS01 · Companies House filing · Directors &amp; Shareholders · SIC codes · Annual review
        </p>
      </div>

      {/* Input tabs */}
      <div style={s.tabBar}>
        <button style={s.tab(tab === 'ch')} onClick={() => setTab('ch')}>🏛 Companies House Lookup</button>
        <button style={s.tab(tab === 'manual')} onClick={() => setTab('manual')}>✏️ Manual Entry</button>
      </div>

      {/* CH Lookup */}
      {tab === 'ch' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Look up from Companies House (free)</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
            Enter the company number to auto-fill company details, registered address, SIC codes and directors from the Companies House public register.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Company Number</label>
              <input style={s.input} value={chNumber} onChange={e => setChNumber(e.target.value)} placeholder="e.g. 12345678" onKeyDown={e => e.key === 'Enter' && handleCHLookup()} />
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <button style={s.btn('primary')} onClick={handleCHLookup} disabled={chLoading}>
                {chLoading ? '⏳ Looking up…' : '🔍 Lookup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry */}
      {tab === 'manual' && (
        <>
          {/* Company Details */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Company Details</div>
            <div style={s.row}>
              <div style={{ flex: 2, minWidth: 200 }}>
                <label style={s.label}>Company Name</label>
                <input style={s.input} value={company.name} onChange={e => setC('name', e.target.value)} placeholder="e.g. Acme Trading Ltd" />
              </div>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={s.label}>Company Number</label>
                <input style={s.input} value={company.number} onChange={e => setC('number', e.target.value)} placeholder="e.g. 12345678" />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={s.label}>Incorporation Date</label>
                <input style={s.input} type="date" value={company.incorporation_date} onChange={e => setC('incorporation_date', e.target.value)} />
              </div>
            </div>
            <div style={s.row}>
              <div style={{ flex: 3, minWidth: 260 }}>
                <label style={s.label}>Registered Address</label>
                <input style={s.input} value={company.registered_address} onChange={e => setC('registered_address', e.target.value)} placeholder="e.g. 123 High Street, London, E1 6RF" />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={s.label}>Confirmation Date</label>
                <input style={s.input} type="date" value={company.confirmation_date} onChange={e => setC('confirmation_date', e.target.value)} />
              </div>
            </div>
            <div style={s.row}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={s.label}>Review Period Start</label>
                <input style={s.input} type="date" value={company.review_period_start} onChange={e => setC('review_period_start', e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <label style={s.label}>Review Period End</label>
                <input style={s.input} type="date" value={company.review_period_end} onChange={e => setC('review_period_end', e.target.value)} />
              </div>
            </div>

            {/* SIC Codes */}
            <div style={{ ...s.sectionTitle, marginTop: 8 }}>SIC Codes</div>
            <input style={{ ...s.input, marginBottom: 12, maxWidth: 300 }} value={sicSearch} onChange={e => setSicSearch(e.target.value)} placeholder="Search SIC codes…" />
            <div style={{ marginBottom: 8 }}>
              {filteredSic.map(({ code, desc }) => (
                <span key={code} style={s.sicChip(company.sic_codes.includes(code))} onClick={() => toggleSic(code)}>
                  {code} — {desc}
                </span>
              ))}
            </div>
            {company.sic_codes.length > 0 && (
              <div style={{ fontSize: 12, color: '#C9973A', marginTop: 6 }}>
                Selected: {company.sic_codes.join(', ')}
              </div>
            )}
          </div>

          {/* Directors */}
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={s.sectionTitle}>Directors &amp; Officers ({directors.length})</div>
              <button style={s.btn('primary')} onClick={addDir}>+ Add Director</button>
            </div>
            {directors.map((d, i) => (
              <div key={i} style={{ padding: 14, borderRadius: 8, background: '#0B1929', border: '1px solid rgba(255,255,255,0.07)', marginBottom: 10 }}>
                <div style={s.row}>
                  <div style={{ flex: 2, minWidth: 180 }}>
                    <label style={s.label}>Full Name</label>
                    <input style={s.input} value={d.name} onChange={e => setDir(i, 'name', e.target.value)} placeholder="e.g. John Smith" />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={s.label}>Role</label>
                    <select style={s.input} value={d.role} onChange={e => setDir(i, 'role', e.target.value)}>
                      <option>Director</option>
                      <option>Secretary</option>
                      <option>Director & Secretary</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={s.label}>Appointed Date</label>
                    <input style={s.input} type="date" value={d.appointed} onChange={e => setDir(i, 'appointed', e.target.value)} />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={s.label}>Nationality</label>
                    <input style={s.input} value={d.nationality} onChange={e => setDir(i, 'nationality', e.target.value)} placeholder="e.g. British" />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={s.label}>Occupation</label>
                    <input style={s.input} value={d.occupation} onChange={e => setDir(i, 'occupation', e.target.value)} placeholder="e.g. Director" />
                  </div>
                  {directors.length > 1 && (
                    <div style={{ alignSelf: 'flex-end' }}>
                      <button style={{ ...s.btn('red'), padding: '8px 12px', fontSize: 11 }} onClick={() => removeDir(i)}>Remove</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Shareholders */}
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={s.sectionTitle}>Shareholders ({shareholders.length})</div>
              <button style={s.btn('primary')} onClick={addSh}>+ Add Shareholder</button>
            </div>
            {shareholders.map((sh, i) => (
              <div key={i} style={{ padding: 14, borderRadius: 8, background: '#0B1929', border: '1px solid rgba(255,255,255,0.07)', marginBottom: 10 }}>
                <div style={s.row}>
                  <div style={{ flex: 2, minWidth: 180 }}>
                    <label style={s.label}>Shareholder Name</label>
                    <input style={s.input} value={sh.name} onChange={e => setSh(i, 'name', e.target.value)} placeholder="e.g. Jane Smith" />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={s.label}>Share Class</label>
                    <select style={s.input} value={sh.share_class} onChange={e => setSh(i, 'share_class', e.target.value)}>
                      <option>Ordinary</option>
                      <option>Preference</option>
                      <option>Ordinary A</option>
                      <option>Ordinary B</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={s.label}>Shares Held</label>
                    <input style={s.input} type="number" value={sh.shares_held} onChange={e => setSh(i, 'shares_held', e.target.value)} placeholder="e.g. 100" />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={s.label}>% Ownership</label>
                    <input style={s.input} type="number" value={sh.percentage} onChange={e => setSh(i, 'percentage', e.target.value)} placeholder="e.g. 100" />
                  </div>
                  {shareholders.length > 1 && (
                    <div style={{ alignSelf: 'flex-end' }}>
                      <button style={{ ...s.btn('red'), padding: '8px 12px', fontSize: 11 }} onClick={() => removeSh(i)}>Remove</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ marginBottom: 20 }}>
        <button style={s.btn('primary')} onClick={handleSubmit} disabled={loading}>
          {loading ? '⏳ Generating…' : '▶ Generate Confirmation Statement'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#f87171', fontSize: 13 }}>⚠ {error}</div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{result.company?.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>
                Co. {result.company?.number || '—'} · Confirmation date: {result.company?.confirmation_date || '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={s.btn('green')} onClick={() => handleExport('excel')}>⬇ Excel</button>
              <button style={s.btn('blue')} onClick={() => handleExport('pdf')}>⬇ PDF</button>
              <button style={s.btn('save')} onClick={handleSave} disabled={saved}>{saved ? '✓ Saved' : '💾 Save'}</button>
            </div>
          </div>

          {/* Company summary */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Company Summary</div>
            {[
              ['Company Name', result.company?.name],
              ['Company Number', result.company?.number],
              ['Registered Address', result.company?.registered_address],
              ['Incorporated', result.company?.incorporation_date],
              ['Confirmation Date', result.company?.confirmation_date],
              ['Review Period', `${result.company?.review_period_start || '—'} to ${result.company?.review_period_end || '—'}`],
              ['SIC Codes', result.company?.sic_codes?.join(', ') || '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)', alignItems: 'center' }}>
                <div style={{ width: 180, fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>{val || '—'}</div>
              </div>
            ))}
          </div>

          {/* Directors */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Directors &amp; Officers</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(201,151,58,0.2)' }}>
                  {['Name', 'Role', 'Appointed', 'Nationality', 'Occupation'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: 'rgba(201,151,58,0.7)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.directors?.map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                    {[d.name, d.role, d.appointed, d.nationality, d.occupation].map((v, ci) => (
                      <td key={ci} style={{ padding: '8px 10px', color: ci === 0 ? '#fff' : 'rgba(255,255,255,0.55)' }}>{v || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Shareholders */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Shareholders</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(201,151,58,0.2)' }}>
                  {['Name', 'Share Class', 'Shares Held', '% Ownership'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: 'rgba(201,151,58,0.7)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.shareholders?.map((sh, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                    {[sh.name, sh.share_class, sh.shares_held, sh.percentage ? `${sh.percentage}%` : '—'].map((v, ci) => (
                      <td key={ci} style={{ padding: '8px 10px', color: ci === 0 ? '#fff' : 'rgba(255,255,255,0.55)' }}>{v || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CS01 Checklist */}
          <div style={s.card}>
            <div style={s.sectionTitle}>CS01 Filing Checklist</div>
            {result.checklist?.map((item, i) => (
              <div key={i} style={s.checkRow(item.status)}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>
                  {item.status === 'ok' ? '✅' : item.status === 'action' ? '⚠️' : 'ℹ️'}
                </span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: item.status === 'ok' ? '#4ade80' : item.status === 'action' ? '#FBBF24' : '#93c5fd' }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
